import React, { useMemo } from 'react';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import GroupsIcon from '@mui/icons-material/Groups';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';

const currency = new Intl.NumberFormat('fr-MA', {
  style: 'currency',
  currency: 'MAD',
  maximumFractionDigits: 2,
});

const ProductSearch = ({
  products,
  categories,
  selectedCategory,
  searchTerm,
  selectedClientType,
  onSearchChange,
  onCategoryChange,
  onClientTypeChange,
  onAdd,
  onPreview,
  loading,
}) => {
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return products.filter((product) => {
      const matchesCategory =
        selectedCategory === 'all' || String(product.category_id || '') === String(selectedCategory);
      if (!matchesCategory) return false;
      if (!term) return true;
      return [product.name, product.sku, product.barcode, product.reference]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [products, searchTerm, selectedCategory]);

  return (
    <div className="pos-products-shell">
      <div className="pos-products-toolbar">
        <div>
          <div className="pos-eyebrow">Catalogue Pegasus</div>
          <h4 className="pos-section-title">Produits</h4>
        </div>
        <div className="pos-search-box">
          <SearchIcon fontSize="small" />
          <input
            type="text"
            placeholder="Produit, SKU ou code-barres"
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            autoFocus
          />
        </div>
      </div>

      <div className="pos-client-row">
        {[
          { id: 'personnal', label: 'Personnel', icon: GroupsIcon },
          { id: 'pickup_client', label: 'Pickup', icon: LocalMallIcon },
          { id: 'delivery_client', label: 'Livraison', icon: DeliveryDiningIcon },
          { id: 'external', label: 'Website / Global', icon: PublicIcon },
        ].map((choice) => {
          const Icon = choice.icon;
          return (
            <button
              type="button"
              key={choice.id}
              title={choice.label}
              className={`pos-client-type ${selectedClientType === choice.id ? 'is-active' : ''}`}
              onClick={() => onClientTypeChange(choice.id)}
            >
              <Icon />
              <span>{choice.label}</span>
            </button>
          );
        })}
      </div>

      <div className="pos-menu-strip">
        <button
          type="button"
          className={`pos-menu-tile ${selectedCategory === 'all' ? 'is-active' : ''}`}
          onClick={() => onCategoryChange('all')}
        >
          <span className="pos-menu-circle">
            <Inventory2OutlinedIcon />
          </span>
          <strong>Tous</strong>
          <small>{products.length}</small>
        </button>
        {categories.map((category) => (
          <button
            type="button"
            key={category._id}
            className={`pos-menu-tile ${String(selectedCategory) === String(category._id) ? 'is-active' : ''}`}
            onClick={() => onCategoryChange(category._id)}
          >
            <span className="pos-menu-circle">
              <span className="pos-menu-fallback">{category.name.slice(0, 1)}</span>
              {category.image ? (
                <img
                  src={category.image}
                  alt=""
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
            </span>
            <strong>{category.name}</strong>
            <small>{category.count}</small>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="pos-empty-state">Chargement des produits...</div>
      ) : (
        <div className="pos-products-grid">
          {filtered.map((product) => (
            <button
              type="button"
              className="pos-product-card"
              key={product._id}
              onClick={() =>
                onAdd({
                  product_id: product._id,
                  name: product.name,
                  unit_price: product.price,
                  image: product.image,
                  sku: product.sku || product.barcode || '',
                })
              }
            >
              <div className="pos-product-image">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <Inventory2OutlinedIcon />
                )}
                {product.image && (
                  <button
                    type="button"
                    className="pos-product-preview"
                    aria-label="Preview product"
                    onClick={(event) => {
                      event.stopPropagation();
                      onPreview?.(product);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </button>
                )}
                <span className="pos-product-add">
                  <AddShoppingCartIcon fontSize="small" />
                </span>
              </div>
              <div className="pos-product-body">
                <div className="pos-product-name">{product.name}</div>
                <div className="pos-product-meta">{product.sku || product.barcode || product.category_name || 'Produit local'}</div>
                <div className="pos-product-price">{currency.format(product.price)}</div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="pos-empty-state">Aucun produit trouve.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
